import React from "react";

// LineChart component
export function LineChart({ 
  data, 
  index, 
  categories,
  colors,
  valueFormatter,
  yAxisWidth
}: {
  data: any[];
  index: string;
  categories: string[];
  colors: string[];
  valueFormatter: (value: number) => string;
  yAxisWidth: number;
}) {
  // This is a minimal implementation for the demo
  const maxValue = Math.max(...data.map(item => item[categories[0]]));
  
  return (
    <div className="w-full h-full">
      <div className="flex h-full">
        {/* Y-axis */}
        <div style={{ width: `${yAxisWidth}px` }} className="pr-2 flex flex-col justify-between text-xs text-muted-foreground">
          <div>{valueFormatter(maxValue)}</div>
          <div>{valueFormatter(maxValue * 0.75)}</div>
          <div>{valueFormatter(maxValue * 0.5)}</div>
          <div>{valueFormatter(maxValue * 0.25)}</div>
          <div>{valueFormatter(0)}</div>
        </div>
        
        {/* Chart area */}
        <div className="flex-1 relative">
          {/* Horizontal grid lines */}
          <div className="absolute inset-0 flex flex-col justify-between">
            <div className="border-t border-dashed border-muted"></div>
            <div className="border-t border-dashed border-muted"></div>
            <div className="border-t border-dashed border-muted"></div>
            <div className="border-t border-dashed border-muted"></div>
            <div className="border-t border-dashed border-muted"></div>
          </div>
          
          {/* Line */}
          <div className="absolute inset-0 flex items-end">
            <svg width="100%" height="100%" viewBox={`0 0 ${data.length} 100`} preserveAspectRatio="none">
              <path
                d={data.map((item, i) => {
                  const x = i;
                  const y = 100 - (item[categories[0]] / maxValue) * 100;
                  return `${i === 0 ? "M" : "L"} ${x} ${y}`;
                }).join(" ")}
                fill="none"
                strokeWidth="2"
                stroke={colors[0]}
              />
            </svg>
          </div>
          
          {/* X-axis labels */}
          <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-muted-foreground pt-2">
            {data.map((item, i) => (
              <div key={i} className={i % 3 === 0 ? "block" : "hidden md:block"}>
                {item[index]}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// BarChart component
export function BarChart({ 
  data, 
  index, 
  categories,
  colors,
  valueFormatter,
  yAxisWidth
}: {
  data: any[];
  index: string;
  categories: string[];
  colors: string[];
  valueFormatter: (value: number) => string;
  yAxisWidth: number;
}) {
  // This is a minimal implementation for the demo
  const maxValue = Math.max(...data.map(item => item[categories[0]]));
  const barWidth = 100 / data.length * 0.6; // 60% of available width per bar
  
  return (
    <div className="w-full h-full">
      <div className="flex h-full">
        {/* Y-axis */}
        <div style={{ width: `${yAxisWidth}px` }} className="pr-2 flex flex-col justify-between text-xs text-muted-foreground">
          <div>{valueFormatter(maxValue)}</div>
          <div>{valueFormatter(maxValue * 0.75)}</div>
          <div>{valueFormatter(maxValue * 0.5)}</div>
          <div>{valueFormatter(maxValue * 0.25)}</div>
          <div>{valueFormatter(0)}</div>
        </div>
        
        {/* Chart area */}
        <div className="flex-1 relative">
          {/* Horizontal grid lines */}
          <div className="absolute inset-0 flex flex-col justify-between">
            <div className="border-t border-dashed border-muted"></div>
            <div className="border-t border-dashed border-muted"></div>
            <div className="border-t border-dashed border-muted"></div>
            <div className="border-t border-dashed border-muted"></div>
            <div className="border-t border-dashed border-muted"></div>
          </div>
          
          {/* Bars */}
          <div className="absolute inset-0 flex items-end">
            <div className="w-full h-full flex justify-around items-end">
              {data.map((item, i) => {
                const height = (item[categories[0]] / maxValue) * 100;
                return (
                  <div 
                    key={i} 
                    className="relative flex flex-col items-center"
                    style={{ width: `${barWidth}%` }}
                  >
                    <div 
                      className="rounded-t-sm"
                      style={{ 
                        height: `${height}%`, 
                        backgroundColor: colors[0],
                        width: '100%'
                      }}
                    ></div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* X-axis labels */}
          <div className="absolute bottom-0 left-0 right-0 flex justify-around text-xs text-muted-foreground pt-2">
            {data.map((item, i) => (
              <div key={i} className="text-center truncate max-w-[80px]">
                {item[index]}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// DonutChart component
export function DonutChart({ 
  data, 
  index, 
  category,
  valueFormatter,
  colors
}: {
  data: any[];
  index: string;
  category: string;
  valueFormatter: (value: number) => string;
  colors: string[];
}) {
  // This is a minimal implementation for the demo
  const total = data.reduce((sum, item) => sum + Math.abs(item[category]), 0);
  let startAngle = 0;
  
  return (
    <div className="w-full h-full flex items-center justify-center">
      <div className="relative" style={{ width: '200px', height: '200px' }}>
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <circle cx="50" cy="50" r="45" fill="none" stroke="#f1f5f9" strokeWidth="10" />
          
          {data.map((item, i) => {
            const value = Math.abs(item[category]);
            const angle = (value / total) * 360;
            const endAngle = startAngle + angle;
            
            // Calculate the SVG arc path
            const x1 = 50 + 45 * Math.cos((startAngle - 90) * (Math.PI / 180));
            const y1 = 50 + 45 * Math.sin((startAngle - 90) * (Math.PI / 180));
            const x2 = 50 + 45 * Math.cos((endAngle - 90) * (Math.PI / 180));
            const y2 = 50 + 45 * Math.sin((endAngle - 90) * (Math.PI / 180));
            
            const largeArcFlag = angle > 180 ? 1 : 0;
            
            const pathData = [
              `M 50 50`,
              `L ${x1} ${y1}`,
              `A 45 45 0 ${largeArcFlag} 1 ${x2} ${y2}`,
              `Z`
            ].join(' ');
            
            const path = (
              <path
                key={i}
                d={pathData}
                fill={colors[i % colors.length]}
              />
            );
            
            startAngle = endAngle;
            return path;
          })}
          
          {/* Inner white circle for donut effect */}
          <circle cx="50" cy="50" r="30" fill="white" />
        </svg>
        
        {/* Center text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold">{data.length}</span>
          <span className="text-xs text-muted-foreground">Categories</span>
        </div>
      </div>
    </div>
  );
}